import React, { useEffect, useState } from "react";
import axios from "../axios";
import AdminBreadcrumb from "../components/AdminBreadcrumb";
import "../styles/admin.css";

const ROLE_CONFIG = {
  1: { label: "スーパー管理者", badge: "adm-badge--red" },
  2: { label: "運用管理者",     badge: "adm-badge--amber" },
  3: { label: "一般",           badge: "adm-badge--blue" },
  4: { label: "BAN",            badge: "adm-badge--red" },
};

export default function AdminUserManagementPage({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const fetchUsers = () => {
    setLoading(true);
    axios
      .get("/users")
      .then((res) => setUsers(res.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const toggleBan = async (user) => {
    const isBanned = user.user_role_id === 4;
    if (!window.confirm(isBanned ? "BAN解除しますか？" : "このユーザをBANしますか？")) return;
    try {
      await axios.put(`/users/${user.id}/ban`, { ban: !isBanned });
      setUsers((prev) =>
        prev.map((u) =>
          u.id === user.id ? { ...u, user_role_id: isBanned ? 3 : 4 } : u
        )
      );
    } catch {
      alert("操作に失敗しました");
    }
  };

  const deletePosts = async (user) => {
    if (user.user_role_id !== 4) {
      alert("BANユーザのみ投稿を削除できます");
      return;
    }
    if (!window.confirm("このユーザの投稿をすべて削除しますか？")) return;
    try {
      await axios.delete(`/admin/users/${user.id}/posts`);
      alert("削除しました");
    } catch {
      alert("削除に失敗しました");
    }
  };

  const deleteUser = async (user) => {
    if (!window.confirm(`「${user.name}」を削除しますか？\nこの操作は取り消せません。`)) return;
    const deletePosts = window.confirm(
      "投稿（目撃情報）はどうしますか？\n\nOK → 投稿も完全削除\nキャンセル → ユーザ情報を匿名化（投稿は退会済みユーザとして保持）"
    );
    try {
      const res = await axios.delete(`/admin/users/${user.id}`, { data: { delete_posts: deletePosts } });
      if (res.data.deleted) {
        setUsers((prev) => prev.filter((u) => u.id !== user.id));
      } else {
        setUsers((prev) => prev.map((u) =>
          u.id === user.id ? { ...u, name: res.data.user.name, email: res.data.user.email, bio: null, activity_area: null, x_id: null, instagram_id: null, website_url: null } : u
        ));
        setExpandedId(null);
      }
    } catch {
      alert("削除に失敗しました");
    }
  };

  const hasProfile = (u) =>
    u.bio || u.activity_area || u.x_id || u.instagram_id || u.website_url;

  return (
    <div className="adm-page adm-page-narrow">
      <AdminBreadcrumb items={[{ label: "ユーザ管理" }]} />

      <div className="adm-header">
        <div className="adm-header-left">
          <h2 className="adm-title">ユーザ管理</h2>
          <span className="adm-badge adm-badge--red">super_admin</span>
        </div>
      </div>

      {loading ? (
        <div className="adm-empty">読み込み中…</div>
      ) : (
        <div className="adm-card">
          <div className="adm-card__header">
            ユーザ一覧
            <span className="adm-card__header-sub">{users.length} 件</span>
          </div>
          <div className="adm-table-wrap">
            <table className="adm-table">
              <thead>
                <tr>
                  {["ID", "名前", "メール", "権限", "状態", "登録日", "メール認証", "操作"].map((h) => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const isBanned = u.user_role_id === 4;
                  const roleCfg = ROLE_CONFIG[u.user_role_id] ?? { label: "不明", badge: "" };
                  const isExpanded = expandedId === u.id;
                  return (
                    <React.Fragment key={u.id}>
                      <tr>
                        <td className="adm-table__id">{u.id}</td>
                        <td className="adm-table__bold adm-nowrap">
                          {hasProfile(u) ? (
                            <button
                              onClick={() => setExpandedId(isExpanded ? null : u.id)}
                              style={{ background: "none", border: "none", cursor: "pointer", padding: 0, textAlign: "left", fontWeight: "700", color: "inherit" }}
                            >
                              {u.name} <span style={{ fontSize: ".7rem", color: "#6b7280" }}>{isExpanded ? "▲" : "▼"}</span>
                            </button>
                          ) : u.name}
                        </td>
                        <td className="adm-table__mute adm-nowrap" style={{ fontSize: ".75rem" }}>{u.email}</td>
                        <td>
                          <span className={`adm-badge ${roleCfg.badge}`}>{roleCfg.label}</span>
                        </td>
                        <td>
                          <span className={`adm-badge ${isBanned ? "adm-badge--red" : "adm-badge--green"}`}>
                            {isBanned ? "BAN済み" : "アクティブ"}
                          </span>
                        </td>
                        <td className="adm-table__mute adm-nowrap" style={{ fontSize: ".75rem" }}>
                          {u.created_at ? u.created_at.slice(0, 10) : "—"}
                        </td>
                        <td className="adm-table__mute adm-nowrap" style={{ fontSize: ".75rem" }}>
                          {u.email_verified_at ? u.email_verified_at.slice(0, 10) : "未認証"}
                        </td>
                        <td>
                          <div className="adm-gap-sm">
                            <button
                              className="adm-btn adm-btn--default adm-btn--sm"
                              onClick={() => toggleBan(u)}
                            >
                              {isBanned ? "BAN解除" : "BAN"}
                            </button>
                            {isBanned && (
                              <button
                                className="adm-btn adm-btn--danger adm-btn--sm"
                                onClick={() => deletePosts(u)}
                              >
                                投稿削除
                              </button>
                            )}
                            {isBanned && (
                              <button
                                className="adm-btn adm-btn--danger adm-btn--sm"
                                onClick={() => deleteUser(u)}
                              >
                                ユーザ削除
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr style={{ background: "#f9fafb" }}>
                          <td colSpan={8} style={{ padding: "10px 16px", fontSize: ".8rem", color: "#374151" }}>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: "12px 24px" }}>
                              {u.activity_area && (
                                <div><span style={{ color: "#6b7280" }}>活動拠点：</span>{u.activity_area}</div>
                              )}
                              {u.bio && (
                                <div><span style={{ color: "#6b7280" }}>自己紹介：</span>{u.bio}</div>
                              )}
                              {u.x_id && (
                                <div><span style={{ color: "#6b7280" }}>X：</span>@{u.x_id}</div>
                              )}
                              {u.instagram_id && (
                                <div><span style={{ color: "#6b7280" }}>Instagram：</span>@{u.instagram_id}</div>
                              )}
                              {u.website_url && (
                                <div><span style={{ color: "#6b7280" }}>Web：</span>
                                  <a href={u.website_url} target="_blank" rel="noopener noreferrer" style={{ color: "#2563eb" }}>
                                    {u.website_url}
                                  </a>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
